import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode.react';
import { SubmitButton } from './SubmitButton';
import { completeMfaSetup, startMfaSetup } from './api';
import { useFormSubmit } from './useFormSubmit';

interface Props {
  session: string;
  email: string;
  onBack: () => void;
  /** Language deliberately picked on this page, if any. */
  chosenLang?: string;
}

/**
 * Answers an MFA_SETUP challenge: the account must enrol an authenticator before it can
 * sign in at all. Without this the user was asked for a code they had no way to produce.
 *
 * Three Cognito steps hide behind this one screen, and each issues a fresh session that
 * the next must carry. The session held here is therefore the one returned by
 * startMfaSetup, not the one passed in as a prop.
 */
export const MfaSetupForm = ({ session, email, onBack, chosenLang }: Props) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [setupSession, setSetupSession] = useState('');
  const { loading, error, setError, runSubmit } = useFormSubmit();
  // Enrolment is started once on mount; React 18 StrictMode double-invokes effects in
  // development, and a second AssociateSoftwareToken would hand back a different secret
  // than the QR already shown.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    startMfaSetup(session)
      .then((result) => {
        setSecretCode(result.secretCode);
        setSetupSession(result.session);
      })
      .catch((e: Error) => setError(e.message));
  }, [session, setError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSubmit(async () => {
      const result = await completeMfaSetup({ session: setupSession, email, code, lang: chosenLang });
      if (result.status === 'OK') {
        globalThis.location.href = '/';
      }
    });
  };

  // Shown in the authenticator app's account list. Both parts are percent-encoded: a local
  // part may legitimately contain '+', '&' or '#', and dropped in raw those end the path or
  // start another query parameter, producing a QR code that scans into a broken entry.
  const account = encodeURIComponent(`cinareo-${email.split('@')[0]}`);
  const otpauth = `otpauth://totp/${account}?secret=${encodeURIComponent(secretCode)}&issuer=Cinareo`;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.mfaSetup.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {secretCode && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <QRCode value={otpauth} size={160} data-testid="mfa-setup-qr" />
            </Box>

            {/* Typing the secret by hand is the fallback when the camera cannot be used. */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('login.mfaSetup.secretLabel')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {secretCode}
              </Typography>
            </Box>

            <TextField
              id="mfa-setup-code"
              label={t('login.mfaSetup.codeLabel')}
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              required
              autoComplete="one-time-code"
              autoFocus
              fullWidth
              size="small"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            />

            <SubmitButton loading={loading} disabled={!code}>
              {t('login.mfaSetup.submit')}
            </SubmitButton>
          </>
        )}

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
